import React, { useEffect, useState, useCallback } from "react";
import type { Control, FieldValues } from "react-hook-form";
import { Controller, useFormContext } from "react-hook-form";
import { Loader } from "@mantine/core";
import SelectField from "./review/SelectField";
import NumberField from "./review/NumberField";
import TextField from "./review/TextField";
import type {
  CommonFieldProps,
  FieldType,
  ReviewItemProps,
  OCRError,
} from "@/pages/ClientProfile/types";
import PasswordField from "./review/PasswordField";
import MultiSelectField from "./review/MultiSelectField";
import { useProfileStore } from "@/pages/ClientProfile/store/profileStore";
import DateField from "./review/DateField";
import { FieldError } from "@pages/ClientProfile/components/TaxInfo/items/FieldError.tsx";
import errorStyles from "@pages/ClientProfile/styles/errorInputStyles.module.scss";
import { useDiagnosticStore } from "@pages/ClientProfile/components/Diagnostics/store";
import SuggestedCorrectionPopover from "@pages/ClientProfile/components/Diagnostics/SuggestedCorrectionPopover";
import { CopyableField } from "../../Diagnostics/CopyableField";
import { useParams } from "react-router-dom";
import { showFieldError } from "@utils/reviewFieldUtils.ts";

const fieldComponents = {
  text: TextField,
  email: TextField,
  date: DateField,
  number: NumberField,
  money: NumberField,
  password: PasswordField,
  select: SelectField,
  multiselect: MultiSelectField,
} as const;

const ReviewItem: React.FC<ReviewItemProps> = React.memo(
  ({ item }) => {
    const { control, setValue } = useFormContext();
    const { fieldType, options } = item.reviewMode ?? {};

    // Update the form value when item.value changes to ensure fresh data
    useEffect(() => {
      setValue(item.key, item.value);
    }, [item.value, item.key, setValue]);

    // Get store state - use specific selectors to minimize re-renders
    const activeDocument = useProfileStore((state) => state.activeDocument);
    const diagnosticErrors = useProfileStore((state) => state.diagnosticErrors);
    const { selectedDiagnosticField, setOpenedPopoverField } =
      useDiagnosticStore();

    // Get store mutation method
    const { userId } = useParams();
    const updateProfileField = useProfileStore(
      (state) => state.updateProfileField
    );

    // Track if this field has been corrected
    const [isCorrected, setIsCorrected] = useState(false);
    const [loadingState, setLoadingState] = useState(false);

    const fieldErrors = diagnosticErrors[item.key] ?? [];
    const hasErrors = fieldErrors.length > 0;
    const isSelectedDiagnosticField = selectedDiagnosticField === item.key;

    // Get suggested correction from OCR errors
    const ocrError = fieldErrors.find(
      (error): error is OCRError => error.type === "ocr_error"
    );

    const suggestedCorrection = ocrError?.suggested;

    // Determine if we should use OCR mutation
    const shouldUseOCR = activeDocument !== null;

    // Extract mutation logic into its own function
    const performFieldUpdate = useCallback(
      async (fieldKey: string, value: any) => {
        // Loading state is already set in handleFillField, so we don't set it again here
        try {
          await updateProfileField(userId ?? "", fieldKey, value);
          // Success notification is shown by the store method
          setLoadingState(false);
        } catch (error: any) {
          showFieldError(error, fieldKey);
          setLoadingState(false);
        }
      },
      [updateProfileField, userId]
    );

    // Auto-open popover when field is selected and has suggested correction
    useEffect(() => {
      if (isSelectedDiagnosticField && suggestedCorrection) {
        setOpenedPopoverField(item.key);
      }
    }, [
      isSelectedDiagnosticField,
      suggestedCorrection,
      item.key,
      setOpenedPopoverField,
    ]);

    // Move useCallback hooks to component level
    const handleFillField = useCallback(
      (suggestedCorrection: string, field: any) => {
        // Set loading state first to prevent rapid changes
        setLoadingState(true);

        // Update the form field value using React Hook Form
        field.onChange(suggestedCorrection);

        // Close popover and mark as corrected
        setOpenedPopoverField(null);
        setIsCorrected(true);

        // Perform the mutation
        performFieldUpdate(item.key, suggestedCorrection);
      },
      [performFieldUpdate, setOpenedPopoverField, item.key]
    );

    const handlePopoverClose = useCallback(
      (field: any) => {
        // Save current field value and close popover
        if (!loadingState) {
          handleFillField(field.value, field);
        }
      },
      [loadingState, handleFillField]
    );

    const commonProps: CommonFieldProps = {
      name: item.key,
      label: item.label,
      placeholder: item.reviewMode?.placeholder ?? item.label,
      required: item.reviewMode?.required,
      size: "md" as const,
      error: hasErrors && !isCorrected,
      disabled: loadingState,
      rightSection: loadingState ? <Loader size="xs" /> : null,
      classNames: {
        input:
          hasErrors && !isCorrected
            ? isSelectedDiagnosticField
              ? errorStyles.errorFieldInputSolid
              : errorStyles.errorFieldInput
            : "",
      },
      styles: {
        input: {
          backgroundColor: isCorrected
            ? "#F0F9F0"
            : selectedDiagnosticField === item.key
              ? "#FEE9E7"
              : "",
          borderColor: isCorrected ? "#22C55E" : "",
          borderWidth: isCorrected ? "2px" : "",
          opacity: loadingState ? 0.6 : 1,
        },
      },
    };

    // Check if key contains 'ssn' or 'bank' (case insensitive)
    const FieldComponent = React.useMemo(() => {
      if (item.key?.toLowerCase() === "ssn") {
        return PasswordField;
      }
      if (fieldType && fieldType in fieldComponents) {
        return fieldComponents[fieldType];
      }
      return TextField;
    }, [item.key, fieldType]);

    return (
      <Controller
        name={item.key}
        control={control as Control<FieldValues>}
        render={({ field }) => {
          return (
            <FieldError
              fieldKey={item.key}
              fieldType={fieldType}
              className="tax-section-data-item"
            >
              {suggestedCorrection ? (
                <SuggestedCorrectionPopover
                  fieldKey={item.key}
                  onClose={() => handlePopoverClose(field)}
                  copyableField={
                    <CopyableField
                      value={suggestedCorrection}
                      maxWidth="100%"
                      isSuggested
                      onClick={() =>
                        handleFillField(suggestedCorrection, field)
                      }
                      disabled={loadingState}
                      copyTexts={{
                        copy: "Click to fill in Wizzie's suggested correction",
                        copied: "Suggested correction filled in",
                      }}
                    />
                  }
                >
                  <div>
                    <FieldComponent
                      commonProps={commonProps}
                      field={field}
                      fieldType={
                        fieldType &&
                        (["text", "email", "date"] as FieldType[]).includes(
                          fieldType
                        )
                          ? (fieldType as "text" | "email" | "date")
                          : undefined
                      }
                      options={options}
                      item={item}
                      mutationType={shouldUseOCR ? "ocr" : "profile"}
                      formId={shouldUseOCR ? activeDocument : undefined}
                    />
                  </div>
                </SuggestedCorrectionPopover>
              ) : (
                <FieldComponent
                  commonProps={commonProps}
                  field={field}
                  fieldType={
                    fieldType &&
                    (["text", "email", "date"] as FieldType[]).includes(
                      fieldType
                    )
                      ? (fieldType as "text" | "email" | "date")
                      : undefined
                  }
                  options={options}
                  item={item}
                  mutationType={shouldUseOCR ? "ocr" : "profile"}
                  formId={shouldUseOCR ? activeDocument : undefined}
                />
              )}
            </FieldError>
          );
        }}
      />
    );
  },
  (prevProps, nextProps) => {
    // Custom comparison function to prevent unnecessary re-renders
    // Only re-render if the item properties that actually matter have changed
    return (
      prevProps.item.key === nextProps.item.key &&
      prevProps.item.value === nextProps.item.value && // Critical: compare values for field updates
      prevProps.item.label === nextProps.item.label &&
      JSON.stringify(prevProps.item.reviewMode) ===
        JSON.stringify(nextProps.item.reviewMode)
    );
  }
);

ReviewItem.displayName = "ReviewItem";

export default ReviewItem;
